import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CrossAggregation } from '../../types/domain';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type UseCrossAggregationComparisonsData = {
  isLoading: boolean;
  aggregationsMap: Record<string, Map<string, Map<string, number>>>;
};

const EMPTY_DATA = new Map<string, Map<string, never>>();

const useCrossAggregationComparisonsData =
  (): UseCrossAggregationComparisonsData => {
    const { currentSimDataId } = useCurrentSimDataId();
    const { comparisons } = useSchemaComparison();
    const { apiFilter } = useApiDataFilter();

    const simDataIds = currentSimDataId
      ? [currentSimDataId, ...comparisons]
      : [];

    const results = useQueries({
      queries: simDataIds.map((simDataId: string) => {
        return {
          queryKey: ['crossAggregation', simDataId, apiFilter],
          queryFn: async ({
            signal,
          }: QueryFunctionContext): Promise<CrossAggregation[]> => {
            return dataApiClient.getAgentCrossAggr(
              simDataId,
              apiFilter,
              signal
            );
          },
          meta: { dataType: 'others' },
        };
      }),
    });

    return {
      isLoading: results.some((result: UseQueryResult): boolean => {
        return result.isFetching;
      }),
      aggregationsMap: Object.fromEntries(
        simDataIds.map(
          (
            simDataId: string,
            index: number
          ): [string, Map<string, Map<string, number>>] => {
            const data = results[index].data;
            if (!data) {
              return [simDataId, EMPTY_DATA];
            }
            const groups = new Map<string, Map<string, number>>();
            for (const item of data) {
              const group = groups.get(item.label);
              if (group) {
                group.set(item.sub_label, item.value);
              } else {
                groups.set(item.label, new Map([[item.sub_label, item.value]]));
              }
            }
            return [simDataId, groups];
          }
        )
      ),
    };
  };

export { useCrossAggregationComparisonsData };
